import React, { useEffect, useState } from "react";

import moment from "moment";
import { OrderProps } from "../../types/order";

type Props = {
  order: OrderProps;
};

// A6 size in mm: 105mm x 148mm
// Convert to pixels at 96 DPI: 105mm = 397px, 148mm = 559px
const A6_WIDTH = "105mm";
const A6_HEIGHT = "148mm";

const OrderPdf = ({ order }: Props) => {
  const companyName = process.env.REACT_APP_COMPANY_NAME_EN;

  const [rotate, setRotate] = useState(false);

  useEffect(() => {
    if (order?.image) {
      const img = new Image();
      img.onload = () => {
        if (img.height > img.width) {
          setRotate(true);
        }
      };
      img.src = order.image;
    }
  }, [order?.image]);

  return (
    <div
      className="print-container"
      style={{
        width: A6_WIDTH,
        height: A6_HEIGHT,
        maxWidth: A6_WIDTH,
        maxHeight: A6_HEIGHT,
        padding: "5mm",
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* Header */}
      <h1
        style={{
          fontSize: "18px",
          fontWeight: "bold",
          color: "black",
          textAlign: "center",
          margin: "0 0 8px 0",
        }}
      >
        {companyName}
      </h1>

      {/* Order Info */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <p style={{ fontSize: "12px", color: "black", margin: 0 }}>
            <span style={{ fontWeight: "bold" }}>પાર્ટી</span>:{" "}
            {order?.party?.firm_name}
          </p>
          <p style={{ fontSize: "12px", color: "black", margin: 0 }}>
            <span style={{ fontWeight: "bold" }}>તારીખ</span>:{" "}
            {order?.created_at
              ? moment(order?.created_at, "YYYY-MM-DD HH:mm:ss").format(
                  "DD-MM-YYYY"
                )
              : ""}
          </p>
          <p style={{ fontSize: "12px", color: "black", margin: 0 }}>
            <span style={{ fontWeight: "bold" }}>સમય</span>:{" "}
            {order?.created_at
              ? moment(order?.created_at, "YYYY-MM-DD HH:mm:ss").format(
                  "hh:mm A"
                )
              : ""}
          </p>
        </div>
        <p style={{ fontSize: "12px", color: "black", margin: 0 }}>
          <span style={{ fontWeight: "bold" }}>ઓર્ડર નં.</span>: {order?.id}
        </p>
      </div>

      {/* Image */}
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          marginBottom: "8px",
          minHeight: "180px",
          maxHeight: "220px",
        }}
      >
        {order?.image && (
          <img
            src={`${order.image}?order=${order.id}`}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              transform: rotate ? "rotate(90deg)" : "none",
            }}
          />
        )}
      </div>

      {/* Table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: "auto",
        }}
      >
        <tbody>
          <tr>
            <td
              colSpan={1}
              style={{
                fontSize: "11px",
                color: "black",
                padding: "4px 6px",
                fontWeight: "bold",
                border: "1px solid black",
                width: "70px",
              }}
            >
              આઈટમ
            </td>
            <td
              colSpan={3}
              style={{
                fontSize: "11px",
                color: "black",
                padding: "4px 6px",
                fontWeight: "600",
                border: "1px solid black",
              }}
            >
              {order?.item?.name || ""}
            </td>
          </tr>
          <tr>
            <td
              colSpan={1}
              style={{
                fontSize: "11px",
                color: "black",
                padding: "4px 6px",
                fontWeight: "bold",
                border: "1px solid black",
              }}
            >
              નંગ
            </td>
            <td
              colSpan={3}
              style={{
                fontSize: "11px",
                color: "black",
                padding: "4px 6px",
                fontWeight: "600",
                border: "1px solid black",
              }}
            >
              {order?.qty || 0}
            </td>
          </tr>

          {order?.item?.isinset ? (
            <React.Fragment>
            <tr>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  border: "1px solid black",
                }}
              >
                કેશ નંગ
              </td>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "600",
                  border: "1px solid black",
                  width: "calc(50% - 70px)",
                }}
              >
                {order?.set_delivered_qty ? order.set_delivered_qty : ""}
              </td>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  border: "1px solid black",
                  width: "70px",
                }}
              >
                કેશ વજન
              </td>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "600",
                  border: "1px solid black",
                  width: "calc(50% - 70px)",
                }}
              >
                {order?.set_delivered_weight
                  ? `${order.set_delivered_weight.toFixed(3)} કિલો`
                  : ""}
              </td>
            </tr>
            <tr>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  border: "1px solid black",
                }}
              >
                પટ્ટા નંગ
              </td>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "600",
                  border: "1px solid black",
                  width: "calc(50% - 70px)",
                }}
              >
                {order?.delivered_qty ? order.delivered_qty : ""}
              </td>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  border: "1px solid black",
                  width: "70px",
                }}
              >
                પટ્ટા વજન
              </td>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "600",
                  border: "1px solid black",
                  width: "calc(50% - 70px)",
                }}
              >
                {order?.delivered_weight
                  ? `${order.delivered_weight.toFixed(3)} કિલો`
                  : ""}
              </td>
            </tr>
            </React.Fragment>
          ) : (
            <React.Fragment>
            <tr>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  border: "1px solid black",
                }}
              >
                ડિ. નંગ
              </td>
              <td
                colSpan={3}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "600",
                  border: "1px solid black",
                }}
              >
                {order?.delivered_qty ? order.delivered_qty : ""}
              </td>
            </tr>
            <tr>
              <td
                colSpan={1}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  border: "1px solid black",
                }}
              >
                ડિ. વજન
              </td>
              <td
                colSpan={3}
                style={{
                  fontSize: "11px",
                  color: "black",
                  padding: "4px 6px",
                  fontWeight: "600",
                  border: "1px solid black",
                }}
              >
                {order?.delivered_weight
                  ? `${order.delivered_weight.toFixed(3)} કિલો`
                  : ""}
              </td>
            </tr>
            </React.Fragment>
          )}
        </tbody>
      </table>
    </div>
  );
};
export default OrderPdf;